import React, { useState, useEffect } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { LANGUAGES } from "../i18n";

const OTHER_LANGS = LANGUAGES.filter((l) => l.code !== "tr");

export default function ProjectTranslations({ projects, onSaved }) {
  const [selId, setSelId] = useState("");
  const [titles, setTitles] = useState({});
  const [saving, setSaving] = useState(false);
  const project = projects.find((p) => p.id === selId);

  const pick = (id) => {
    setSelId(id);
    const p = projects.find((x) => x.id === id);
    const i18n = (p && p.title_i18n) || {};
    setTitles({ ...i18n, en: i18n.en || (p && p.title_en) || "" });
  };

  useEffect(() => {
    if (projects.length && !projects.find((p) => p.id === selId)) pick(projects[0].id);
  }, [projects]); // eslint-disable-line

  const save = async () => {
    if (!project) return;
    setSaving(true);
    try {
      const clean = {};
      OTHER_LANGS.forEach(({ code }) => {
        const v = (titles[code] || "").trim();
        if (v) clean[code] = v;
      });
      const payload = {
        title: project.title,
        title_en: clean.en || project.title_en || "",
        title_i18n: clean,
        category: project.category,
        status: project.status || "Tamamlanan",
        location: project.location,
        description: project.description || "",
        description_en: project.description_en || "",
        image_url: project.image_url || "",
        gallery: project.gallery || [],
        youtube_urls: project.youtube_urls || [],
        year: project.year || "",
        featured: !!project.featured,
      };
      await api.put(`/projects/${project.id}`, payload);
      toast.success("Çeviriler kaydedildi");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="translations-panel">
      <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-white">Proje Çevirileri</h2>
          <p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">
            Proje isimlerinin her dildeki karşılığını girin. Boş bırakılan diller Türkçe ismi gösterir.
          </p>
        </div>
      </div>
      <div className="my-6 h-px bg-[var(--border)]" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 border border-[var(--border)] max-h-[60vh] overflow-y-auto" data-testid="translations-project-list">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => pick(p.id)}
              data-testid={`translate-project-${p.id}`}
              className={`w-full text-left px-4 py-3 text-sm border-b border-[var(--border)] transition-colors ${
                selId === p.id ? "bg-white text-black" : "text-white hover:bg-[var(--surface)]"
              }`}
            >
              {p.title}
              {p.title_i18n && Object.keys(p.title_i18n).length > 0 && (
                <span className={`ml-2 text-[10px] uppercase tracking-widest ${selId === p.id ? "text-black/50" : "text-[var(--text-muted)]"}`}>
                  {Object.keys(p.title_i18n).length} dil
                </span>
              )}
            </button>
          ))}
        </div>

        {project && (
          <div className="lg:col-span-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">Türkçe (Ana İsim)</label>
              <input value={project.title} disabled className="input-sharp opacity-60" data-testid="translate-title-tr" />
            </div>
            {OTHER_LANGS.map(({ code, label }) => (
              <div key={code}>
                <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{label} ({code.toUpperCase()})</label>
                <input
                  value={titles[code] || ""}
                  onChange={(e) => setTitles((s) => ({ ...s, [code]: e.target.value }))}
                  placeholder={project.title}
                  className="input-sharp"
                  data-testid={`translate-title-${code}`}
                />
              </div>
            ))}
            <div className="flex justify-end pt-2">
              <button onClick={save} disabled={saving} className="btn-sharp btn-primary !py-3 !px-5 !text-xs disabled:opacity-50" data-testid="translations-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Kaydet
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
